import React, { useState } from "react";
import LeadCard from "../LeadCard/LeadCard";
import styles from "./LeadColumn.module.css";

/**
 * LeadColumn — coluna do kanban com header colorido e lista de cards.
 *
 * Props:
 *   stage             {{ id, label, emoji, accent, accentSoft }}  — config da coluna
 *   leads             {Array}                                     — leads desta coluna
 *   loading           {boolean}                                   — exibe skeletons
 *   stages            {Array}                                     — lista completa (para menu Mover para…)
 *   onOpenLead        {(lead) => void}
 *   onMoveLead        {(lead, toStage) => void}
 *   onDeleteLead      {(lead) => void}
 *   membersByUserId   {Record<string, Object>}                    — mapa userId → TenantMember (resolve `lead.assignedTo`)
 *   isDeemphasized    {boolean}                                   — visualmente menos destacada (ex.: LOST)
 *   collapsible       {boolean}                                   — quando true, header é clicável e expande/recolhe
 *   defaultCollapsed  {boolean}                                   — estado inicial quando `collapsible`
 */
export default function LeadColumn({
  stage,
  leads = [],
  loading = false,
  stages = [],
  onOpenLead,
  onMoveLead,
  onDeleteLead,
  membersByUserId = {},
  isDeemphasized = false,
  collapsible = false,
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = useState(
    collapsible ? defaultCollapsed : false,
  );
  const count = leads.length;

  const handleToggle = () => {
    if (!collapsible) return;
    setCollapsed((c) => !c);
  };

  const handleHeaderKeyDown = (e) => {
    if (!collapsible) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const headerProps = collapsible
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handleToggle,
        onKeyDown: handleHeaderKeyDown,
        "aria-expanded": !collapsed,
        "aria-controls": `lead-column-body-${stage.id}`,
        className: `${styles.header} ${styles.headerCollapsible}`,
      }
    : { className: styles.header };

  return (
    <section
      className={`${styles.column} ${isDeemphasized ? styles.deemphasized : ""} ${
        collapsible && collapsed ? styles.columnCollapsed : ""
      }`}
      aria-label={`Coluna ${stage.label}`}
    >
      <header
        {...headerProps}
        style={{
          background: stage.accentSoft,
          borderTop: `3px solid ${stage.accent}`,
        }}
      >
        <span className={styles.headerTitle}>
          {collapsible && (
            <span className={styles.collapseIcon} aria-hidden="true">
              {collapsed ? "▶" : "▼"}
            </span>
          )}
          <span aria-hidden="true">{stage.emoji}</span> {stage.label}
        </span>
        <span className={styles.count} aria-label={`${count} leads`}>
          {count}
        </span>
      </header>

      {!collapsed && (
        <div
          className={styles.cards}
          id={collapsible ? `lead-column-body-${stage.id}` : undefined}
        >
          {loading &&
            Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={styles.skeleton} aria-hidden="true" />
            ))}

          {!loading && count === 0 && (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden="true">
                📭
              </span>
              Nenhum lead aqui ainda
            </div>
          )}

          {!loading &&
            leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                assignee={
                  lead.assignedTo ? membersByUserId[lead.assignedTo] || null : null
                }
                stages={stages}
                currentStage={stage.id}
                onOpen={onOpenLead}
                onMove={onMoveLead}
                onDelete={onDeleteLead}
              />
            ))}
        </div>
      )}
    </section>
  );
}
